import {
	EmissionValueAnsweredUnit,
	type EmissionValueUnit,
} from '@api-client/api'
import {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useContext,
	useState,
} from 'react'

// Maps every EmissionValueAnsweredUnit to its display string.
// Extends the EmissionValueUnit-based UNIT_ADORNMENT with imperial units
// (lbs, mi, ft², ft³, lbs·mi) that exist only in EmissionValueAnsweredUnit.
export const ANSWERED_UNIT_ADORNMENT: Partial<
	Record<EmissionValueAnsweredUnit, string>
> = {
	[EmissionValueAnsweredUnit.KGCO2_EQ_KG]: 'kgCO2eq / kg',
	[EmissionValueAnsweredUnit.KGCO2_EQ_KM]: 'kgCO2eq / km',
	[EmissionValueAnsweredUnit.KGCO2_EQ_GB]: 'kgCO2eq / GB',
	[EmissionValueAnsweredUnit.KGCO2_EQ_M2]: 'kgCO2eq / m\u00B2',
	[EmissionValueAnsweredUnit.KGCO2_EQ_M3]: 'kgCO2eq / m\u00B3',
	[EmissionValueAnsweredUnit.KGCO2_EQ_KWH]: 'kgCO2eq / kWh',
	[EmissionValueAnsweredUnit.KGCO2_EQ_KG_KM]: 'kgCO2eq / kg\u00B7km',
	[EmissionValueAnsweredUnit.KGCO2_EQ_DAY]: 'kgCO2eq / day',
	[EmissionValueAnsweredUnit.KGCO2_EQ_USD]: 'kgCO2eq / USD',
	[EmissionValueAnsweredUnit.KGCO2_EQ_EUR]: 'kgCO2eq / EUR',
	[EmissionValueAnsweredUnit.KGCO2_EQ_DIMENSIONLESS]: 'kgCO2eq / item',
	[EmissionValueAnsweredUnit.KGCO2_EQ_ITEM]: 'kgCO2eq / item',
	[EmissionValueAnsweredUnit.KGCO2_EQ_MI]: 'kgCO2eq / mi',
	[EmissionValueAnsweredUnit.KGCO2_EQ_LBS]: 'kgCO2eq / lbs',
	[EmissionValueAnsweredUnit.KGCO2_EQ_FT2]: 'kgCO2eq / ft\u00B2',
	[EmissionValueAnsweredUnit.KGCO2_EQ_FT3]: 'kgCO2eq / ft\u00B3',
	[EmissionValueAnsweredUnit.KGCO2_EQ_LBS_MI]: 'kgCO2eq / lbs\u00B7mi',
}

const AnsweredUnitContext = createContext<
	EmissionValueAnsweredUnit | undefined
>(undefined)

const SetAnsweredUnitContext = createContext<
	Dispatch<SetStateAction<EmissionValueAnsweredUnit | undefined>>
>(() => undefined)

interface AnsweredUnitProviderProps {
	children: ReactNode
}

export const AnsweredUnitProvider = ({
	children,
}: AnsweredUnitProviderProps) => {
	const [answeredUnit, setAnsweredUnit] = useState<
		EmissionValueAnsweredUnit | undefined
	>(undefined)

	return (
		<SetAnsweredUnitContext.Provider value={setAnsweredUnit}>
			<AnsweredUnitContext.Provider value={answeredUnit}>
				{children}
			</AnsweredUnitContext.Provider>
		</SetAnsweredUnitContext.Provider>
	)
}

export const useAnsweredUnit = (): EmissionValueAnsweredUnit | undefined =>
	useContext(AnsweredUnitContext)

export const useSetAnsweredUnit = (): Dispatch<
	SetStateAction<EmissionValueAnsweredUnit | undefined>
> => useContext(SetAnsweredUnitContext)

// Re-export EmissionValueUnit for consumers that need to work with product units
export type { EmissionValueUnit }
